import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export const SUPPORTED_CODECS = [
  { id: "PCMU", label: "PCMU (G.711 µ-law)" },
  { id: "PCMA", label: "PCMA (G.711 A-law)" },
  { id: "G729", label: "G.729" },
  { id: "G722", label: "G.722 (HD)" },
  { id: "OPUS", label: "Opus" },
  { id: "GSM", label: "GSM" },
  { id: "ILBC", label: "iLBC" },
  { id: "TELEPHONE-EVENT", label: "telephone-event (DTMF RFC 2833)" },
];

const ALL_IDS = SUPPORTED_CODECS.map((c) => c.id);

/**
 * Codec policy editor used in Customer + Trunk dialogs.
 *
 * Props:
 *   mode:     "allow" | "deny"
 *   codecs:   string[]  — the CHECKED set under whichever mode is active.
 *                          In "allow" mode these are the ONLY codecs offered.
 *                          In "deny"  mode these are STRIPPED from SDP.
 *   onChange: ({mode, codecs}) => void
 *   idPrefix: prefix used for data-testids
 *
 * When the user toggles Allow ↔ Deny, we invert the selection so the visible
 * ticked set always reflects "what this mode means": allow-mode ticks are the
 * codecs that survive; deny-mode ticks are the codecs that get dropped. This
 * keeps the two modes from looking like duplicates of each other while
 * preserving the user's intent (an invert is semantically lossless).
 */
export function CodecPolicyEditor({ mode, codecs, onChange, idPrefix = "codec" }) {
  const safeMode = mode === "deny" ? "deny" : "allow";
  const safeCodecs = Array.isArray(codecs) ? codecs : [];

  const toggle = (cid) => {
    const exists = safeCodecs.includes(cid);
    const next = exists ? safeCodecs.filter((c) => c !== cid) : [...safeCodecs, cid];
    onChange && onChange({ mode: safeMode, codecs: next });
  };

  const setMode = (nextMode) => {
    if (nextMode === safeMode || !onChange) return;
    // Invert the selection: what was "allow these 2" becomes "deny the other 6"
    // — semantically equivalent but now visually reflects the new meaning.
    const inverted = ALL_IDS.filter((id) => !safeCodecs.includes(id));
    onChange({ mode: nextMode, codecs: inverted });
  };

  // Color the ticked codecs differently per mode so the intent is obvious:
  //  • allow-mode tick = codec survives the negotiation  → green
  //  • deny-mode  tick = codec is removed from SDP        → red
  const tickedColorClass =
    safeMode === "allow"
      ? "text-[hsl(var(--ok))]"
      : "text-[hsl(var(--danger))]";

  const denyModeActive = safeMode === "deny";

  return (
    <div className="space-y-3 rounded-md border border-border/60 p-3 bg-muted/20">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Codec policy</Label>
        <div
          className="inline-flex rounded-md border border-border overflow-hidden text-xs"
          data-testid={`${idPrefix}-mode-switch`}
        >
          <button
            type="button"
            className={`px-3 py-1 transition-colors ${
              safeMode === "allow"
                ? "bg-[hsl(var(--ok))]/15 text-[hsl(var(--ok))] font-medium"
                : "bg-transparent text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setMode("allow")}
            data-testid={`${idPrefix}-mode-allow`}
          >
            Allow list
          </button>
          <button
            type="button"
            className={`px-3 py-1 transition-colors ${
              safeMode === "deny"
                ? "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] font-medium"
                : "bg-transparent text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setMode("deny")}
            data-testid={`${idPrefix}-mode-deny`}
          >
            Deny list
          </button>
        </div>
      </div>

      {/* Mode-specific helper copy — different for each mode so users can
          never mistake one for the other. */}
      <p className="text-[11px] text-muted-foreground -mt-1">
        {denyModeActive ? (
          <>
            <span className="font-medium text-foreground">Check a codec to BLOCK it.</span>{" "}
            Unchecked codecs are still negotiated. Switching to <em>Allow list</em>{" "}
            inverts your selection automatically.
          </>
        ) : (
          <>
            <span className="font-medium text-foreground">Check a codec to ALLOW it.</span>{" "}
            Everything unchecked is rejected. Switching to <em>Deny list</em>{" "}
            inverts your selection automatically.
          </>
        )}
      </p>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
        {SUPPORTED_CODECS.map((c) => {
          const checked = safeCodecs.includes(c.id);
          return (
            <label
              key={c.id}
              className="flex items-center gap-2 text-xs cursor-pointer select-none"
              data-testid={`${idPrefix}-codec-${c.id}`}
            >
              <Checkbox checked={checked} onCheckedChange={() => toggle(c.id)} />
              <span
                className={
                  checked
                    ? `${tickedColorClass} font-medium`
                    : "text-muted-foreground line-through decoration-muted-foreground/40"
                }
              >
                {c.label}
              </span>
            </label>
          );
        })}
      </div>

      {/* Live summary so the effective policy is always visible. */}
      <div
        className="text-[11px] rounded-md border border-border/50 bg-background/40 px-2 py-1.5"
        data-testid={`${idPrefix}-effective-summary`}
      >
        <span className="text-muted-foreground">Effective: </span>
        {denyModeActive ? (
          <>
            <span className="text-[hsl(var(--danger))] font-medium">block</span>{" "}
            {safeCodecs.length ? safeCodecs.join(", ") : (
              <span className="italic text-muted-foreground">nothing blocked (all codecs negotiated)</span>
            )}
          </>
        ) : (
          <>
            <span className="text-[hsl(var(--ok))] font-medium">only</span>{" "}
            {safeCodecs.length ? safeCodecs.join(", ") : (
              <span className="italic text-[hsl(var(--danger))]">
                nothing allowed — this will reject ALL calls. Tick at least one.
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
